<form
  [formGroup]="formGroup"
  (keydown.enter)="$event.preventDefault()"
  *ngIf="formGroup"
>
  <!-- Minor Amendment restriction banner -->
  <div
    *ngIf="status?.isMinorAmendment"
    class="alert d-flex align-items-center gap-2 mb-2"
    style="
      background-color: #fff3e0;
      border: 1px solid #f57c00;
      border-radius: 4px;
      padding: 10px 14px;
    "
  >
    <mat-icon style="color: #f57c00; flex-shrink: 0">warning</mat-icon>
    <span>
      <strong>Minor Amendment — Pre-approved Changes Only.</strong>
      Only the <strong>Work Description text</strong> and
      <strong>Estimated Dates</strong> may be edited. Changing shielding
      mentions in the Work Description is not allowed. All other fields are
      locked. To modify restrictions, locations, shielding, requester,
      parent/child links, testing requirements, functional tests, or hold
      points, you must create a <strong>Full Amendment</strong>.
    </span>
  </div>

  <div class="d-flex justify-content-between align-items-center mt-2">
    <h5 class="mb-0">Work Description and Notifications</h5>
    <!-- Tester tool (non-production, System Administrators only, Tester Tools toggle ON): fills the
         Section 1 fields with valid dummy data for one of four workflow paths so the form becomes a
         submittable Draft in one click. Does not save. -->
    <div class="d-print-none" *ngIf="showTestTools && !disabled">
      <button
        mat-stroked-button
        color="warn"
        type="button"
        [matMenuTriggerFor]="autoFillMenu"
        matTooltip="Tester tool (non-production): fills Section 1 with valid dummy data for the selected workflow path. Does not save."
        matTooltipPosition="left"
      >
        <mat-icon>auto_fix_high</mat-icon>
        Auto-populate
      </button>
      <mat-menu #autoFillMenu="matMenu">
        <button mat-menu-item type="button" (click)="autoPopulate('shielding-happy')">
          <mat-icon>sentiment_satisfied</mat-icon>
          <span>Shielding: Happy Path</span>
        </button>
        <button mat-menu-item type="button" (click)="autoPopulate('shielding-complex')">
          <mat-icon>account_tree</mat-icon>
          <span>Shielding: Complex</span>
        </button>
        <button mat-menu-item type="button" (click)="autoPopulate('pps-happy')">
          <mat-icon>sentiment_satisfied</mat-icon>
          <span>PPS: Happy Path</span>
        </button>
        <button mat-menu-item type="button" (click)="autoPopulate('pps-complex')">
          <mat-icon>account_tree</mat-icon>
          <span>PPS: Complex</span>
        </button>
        <button mat-menu-item type="button" (click)="resetFields()">
          <mat-icon>restart_alt</mat-icon>
          <span>Reset (clear all)</span>
        </button>
      </mat-menu>
    </div>
  </div>
  <div class="row">
    <div class="col-6">
      <!-- ASCC Links -->
      <related-documents
        class="mb-1"
        [control]="ctrlASCCLinks"
        [relatedDocuments]="documents"
        [formID]="rsf?.id"
        [formType]="formTypeEnum.RSF"
        [excludeSelf]="true"
        [disabled]="disabled"
        [hilite]="checkDiff(field.ASCCForms)"
        (changed)="
          onRelatedDocumentChanged($event, rsfRelatedDocumentEnum.ASCCForm)
        "
      ></related-documents>
    </div>
    <div class="col-6">
      <!-- Parent Forms -->
      <related-documents
        class="mb-1"
        [control]="ctrlParentForms"
        label="Parent RSF"
        [relatedDocuments]="parentForms"
        [filterBy]="formTypeEnum.RSF"
        [formID]="rsf?.id"
        [formType]="formTypeEnum.RSF"
        [excludeSelf]="true"
        [excludeDocuments]="childrenForms"
        [maxItems]="1"
        [disabled]="disabled || !rsfProxy?.ppsMaintenance"
        [hilite]="checkDiff(field.ChildForms)"
        (changed)="
          onRelatedDocumentChanged($event, rsfRelatedDocumentEnum.ParentForm)
        "
      ></related-documents>
    </div>
  </div>
  <div class="row">
    <div class="col-6">
      <!-- Related Documents and Links -->
      <related-link-chips
        class="mb-1"
        [control]="ctrlRelatedLinks"
        [relatedLinks]="relatedLinks"
        [formId]="rsf?.id"
        [hilite]="checkDiff(field.RelatedLinks)"
        (onEdited)="onRelatedLinksChanged($event)"
        [disabled]="disabled"
      ></related-link-chips>
    </div>
    <div class="col-6">
      <!-- Child Forms -->
      <related-documents
        class="mb-1"
        [control]="ctrlChildForms"
        label="Child RSFs"
        [relatedDocuments]="childrenForms"
        [filterBy]="formTypeEnum.RSF"
        [formID]="rsf?.id"
        [formType]="formTypeEnum.RSF"
        [excludeSelf]="true"
        [excludeDocuments]="parentForms"
        [disabled]="disabled || !rsfProxy?.ppsMaintenance"
        [hilite]="
          rsf && this.status?.isViewingAmendment && checkDiff(field.ChildForms)
        "
        (changed)="
          onRelatedDocumentChanged($event, rsfRelatedDocumentEnum.ChildForm)
        "
      ></related-documents>
    </div>
  </div>

  <rsf-main-s1-systems-affected
    [rsfProxy]="rsfProxy"
    [rsfAmendment]="rsfAmendment"
    [status]="status"
    [disabled]="disabled || !!status?.isMinorAmendment"
    [service]="service"
    [acceleratorLocations]="selectedAcceleratorLocations"
    [beamlineLocations]="selectedBeamlineLocations"
    [checkDiff]="checkDiff"
    [hasErrors]="status?.isLogView ? false : systemsAffectedHaserror"
    [createResourceRelations]="createResourceRelations"
    (changed)="onSystemsAffectedChanged($event)"
    (locationAddedID)="onLocationAddedID($event)"
  ></rsf-main-s1-systems-affected>

  <div class="mt-2">
    <comments-box
      [textValue]="rsfProxy?.description ?? ''"
      [control]="ctrlDescription"
      [disabled]="disabled"
      formType="RSF"
      [hasError]="descriptionHasError"
      [serialNo]="rsf?.serialNo ?? 'RSF'"
      title="Work Description"
      (textChanged)="descriptionChanged($event)"
      (mentionsChanged)="mentionsChanged($event)"
      [mentions]="mentions"
      [strikethroughMentionIds]="unapprovedTocaMentionIds"
      [hilite]="checkDiff(field.WorkDescription)"
      [noSubscript]="!rsf"
      hint="ONLY work on the shielding listed above. Shared Shielding indicated in
        red, TOCAs in yellow, Labeled in black, and Unlabeled in blue."
    ></comments-box>
  </div>
  <div class="row">
    <div class="col-3">
      <mat-form-field
        [ngClass]="{
          hilite: checkDiff(field.Requester),
          'no-subscript': !ctrlRequester.invalid,
        }"
      >
        <mat-label>Requester</mat-label>
        <input
          matInput
          placeholder="Requester"
          aria-label="Requester"
          [matAutocomplete]="reqAuto"
          [formControl]="ctrlRequester"
          name="Requester"
          (input)="onRequesterChange($event)"
          #trigger="matAutocompleteTrigger"
        />
        <mat-autocomplete
          #reqAuto="matAutocomplete"
          [displayWith]="displayObject"
          (optionSelected)="onRequesterSelected($event)"
        >
          <mat-option [value]="user" *ngFor="let user of filteredRequesters">
            <div class="d-flex full-width">
              <span class="mr-2">{{ user.name }}</span>
              <role-chips
                [roleCodes]="getRoleCodes(user)"
                [disabled]="true"
              ></role-chips>
            </div>
          </mat-option>
        </mat-autocomplete>
        <mat-error *ngIf="ctrlRequester.invalid">{{
          getErrorMsg(ctrlRequester)
        }}</mat-error>
      </mat-form-field>
    </div>
    <div class="col-3 offset-3">
      <mat-form-field
        *ngIf="rsf"
        [ngClass]="{
          hilite: checkDiff(field.InitDate),
          'no-subscript': !ctrlInitDate.invalid,
        }"
      >
        <mat-label>Estimate Start Date</mat-label>
        <input
          matInput
          [matDatepicker]="startDate"
          placeholder="Estimate Start Date"
          name="startDate"
          (dateChange)="onInitDateChanged($event)"
          [formControl]="ctrlInitDate"
        />
        <mat-datepicker-toggle
          class="d-print-none"
          matSuffix
          [for]="startDate"
        ></mat-datepicker-toggle>
        <mat-datepicker #startDate></mat-datepicker>
        <mat-error *ngIf="ctrlInitDate.invalid">{{
          getErrorMsg(ctrlInitDate)
        }}</mat-error>
      </mat-form-field>
    </div>
    <div class="col-3">
      <mat-form-field
        *ngIf="rsf"
        [ngClass]="{
          hilite: checkDiff(field.EndDate),
          'no-subscript': !ctrlEndDate.invalid,
        }"
      >
        <mat-label>Estimate Completion Date</mat-label>
        <input
          matInput
          [matDatepicker]="endDate"
          [min]="ctrlInitDate.value"
          placeholder="Estimate Completion Date"
          name="endDate"
          (dateChange)="onEndDateChanged($event)"
          [formControl]="ctrlEndDate"
        />
        <mat-datepicker-toggle
          class="d-print-none"
          matSuffix
          [for]="endDate"
        ></mat-datepicker-toggle>
        <mat-datepicker #endDate></mat-datepicker>
        <mat-error *ngIf="ctrlEndDate.invalid">{{
          getErrorMsg(ctrlEndDate)
        }}</mat-error>
      </mat-form-field>
    </div>
  </div>
  <div class="row">
    <div class="col-12 d-flex align-items-center gap-3">
      <rsf-main-s1-notifications
        class="full-width"
        [rsfProxy]="rsfProxy"
        [rsfAmendment]="rsfAmendment"
        [service]="service"
        [status]="status"
        [disabled]="disabled || !!status?.isMinorAmendment"
        (changed)="onRoleNotificationsChanged($event)"
      ></rsf-main-s1-notifications>
      <rsf-olog-dialog
        *ngIf="rsf"
        [rsf]="rsf"
        [disabled]="disabled"
      ></rsf-olog-dialog>
    </div>
  </div>

  <div *ngIf="rsf">
    <h6 class="mt-3">Safety Controls</h6>
    <form-restriction-table
      [formType]="formTypeEnum.RSF"
      [formID]="rsf.id"
      [locationIDs]="locationIDs"
      [formRestrictions]="formRestrictions"
      [disabled]="disabled || !!status?.isMinorAmendment"
      [customTitle]="'Required controls in place prior to work'"
      [hilite]="checkDiff(field.Restrictions)"
      [control]="ctrlRestrictions"
      [locationAddedIDs]="locationAddedIDs"
      (changed)="onRestrictionsChanged($event)"
      [showDeleted]="status?.isViewingAmendment ?? false"
    ></form-restriction-table>
  </div>
</form>
